import { useState } from 'react';
import type { ReactElement } from 'react';
import { formatDate, formatNumber } from '@xdocs/i18n';
import { EmptyState, useI18n } from '@xdocs/ui';
import { BarChart, KpiTile, RoleGate, useAdminAnalyticsOverview, useAdminAnalyticsTimeseries, useAdminT } from '../../../features/admin/index.js';
import styles from './OverviewPage.module.css';

const TWELVE_WEEKS_MS = 12 * 7 * 24 * 60 * 60 * 1000;

/**
 * `/admin/overview` (F71 client, design/app/38-admin-overview.html): 6 KPI
 * tiles + one time chart, over the cached PG read-models
 * (`admin.analytics.overview`/`.timeseries`).
 *
 * OPEN ISSUE: the mockup's chart is titled "Generation volume · 12 months",
 * but `AdminAnalyticsTimeseriesQuery.granularity` (`packages/schemas/src/
 * admin.ts`) is `'day' | 'week'` only — no `'month'`. This renders the same
 * real op at `granularity: 'week'` over the last 12 weeks instead of
 * fabricating monthly buckets the backend can't produce; the panel title
 * (`admin:overview.chart.title`/`.ariaLabel`) says "12 weeks" to stay honest
 * about what's actually plotted — previously drifted back to a stale
 * "12 months" copy in the catalog while this comment still said "weeks"
 * (fixed: fidelity-gate screenshot review caught the live mismatch). Add
 * `'month'` to the enum (+ the read-model's bucketing) if a true 12-month
 * view is wanted.
 */
export function OverviewPage(): ReactElement {
  return (
    <RoleGate minimum="admin">
      <OverviewContent />
    </RoleGate>
  );
}

function OverviewContent(): ReactElement {
  const { locale, t: tCommon } = useI18n();
  const { t } = useAdminT();
  const { overview, isLoading, isError, error, refetch } = useAdminAnalyticsOverview();

  // BUG FIX (fidelity-gate screenshot review — the chart rendered
  // permanently blank, never settling into data/empty/error): `now`/`from`
  // MUST be stable across renders. `useAdminAnalyticsTimeseries` folds both
  // into its TanStack Query `queryKey` (`.toISOString()`) — recomputing
  // `new Date()` inline on every render (the previous code) mints a NEW
  // queryKey every render, including the re-render the query's OWN
  // pending->success transition triggers, so it never stops refetching
  // ("loading" forever) instead of settling once per page visit. `useState`
  // initializer runs exactly once per mount, same "cached read-model" grain
  // as the 30s `staleTime` already assumes.
  const [now] = useState(() => new Date());
  const [from] = useState(() => new Date(now.getTime() - TWELVE_WEEKS_MS));
  const timeseries = useAdminAnalyticsTimeseries('generation_volume', from, now, 'week');

  if (isError) {
    return <EmptyState kind="error" title={t('admin:overview.error.title')} description={error?.message} action={{ label: tCommon('common:action.retry'), onClick: refetch }} />;
  }

  if (!isLoading && overview && overview.signupsTotal === 0 && overview.dau === 0 && overview.mau === 0) {
    return <EmptyState kind="first-use" title={t('admin:overview.empty.title')} description={t('admin:overview.empty.description')} />;
  }

  const chartData = timeseries.points.map((point) => ({
    label: formatDate(locale, point.bucket, { month: 'short', day: 'numeric' }),
    value: point.value,
  }));

  return (
    <div className={styles['page']}>
      <h1 className={styles['title']}>{t('admin:overview.title')}</h1>
      <p className={styles['subtitle']}>{t('admin:overview.subtitle', { time: overview ? formatDate(locale, overview.generatedAt, { timeStyle: 'short' }) : '—' })}</p>

      <div className={styles['grid']}>
        <KpiTile label={t('admin:overview.kpi.signups')} value={overview ? formatNumber(locale, overview.signupsTotal) : '—'} loading={isLoading} />
        <KpiTile label={t('admin:overview.kpi.dau')} value={overview ? formatNumber(locale, overview.dau) : '—'} loading={isLoading} />
        <KpiTile label={t('admin:overview.kpi.wau')} value={overview ? formatNumber(locale, overview.wau) : '—'} loading={isLoading} />
        <KpiTile label={t('admin:overview.kpi.mau')} value={overview ? formatNumber(locale, overview.mau) : '—'} loading={isLoading} />
        <KpiTile label={t('admin:overview.kpi.creditSpend')} value={overview ? formatNumber(locale, overview.creditSpend) : '—'} loading={isLoading} />
        <KpiTile label={t('admin:overview.kpi.creditEarn')} value={overview ? formatNumber(locale, overview.creditEarn) : '—'} loading={isLoading} />
      </div>

      <div className={styles['chartPanel']}>
        <div className={styles['chartTitle']}>{t('admin:overview.chart.title')}</div>
        {timeseries.isError ? (
          <EmptyState kind="error" title={t('admin:overview.error.title')} action={{ label: tCommon('common:action.retry'), onClick: timeseries.refetch }} />
        ) : !timeseries.isLoading && chartData.length === 0 ? (
          // E19: a chart with zero buckets is an empty state, not a blank
          // box — `BarChart` itself has no "no data" copy of its own (it's a
          // pure rendering primitive over whatever `data` it's given), so
          // the page-level caller owns this distinct-from-error empty case.
          <p className={styles['chartEmpty']}>{t('admin:overview.chart.empty')}</p>
        ) : (
          <BarChart
            data={chartData}
            ariaLabel={t('admin:overview.chart.ariaLabel')}
            valueFormatter={(v) => formatNumber(locale, v)}
            categoryColumnLabel={t('admin:chart.column.category')}
            valueColumnLabel={t('admin:chart.column.value')}
          />
        )}
      </div>
    </div>
  );
}
